import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import { Award, Users, Search } from 'lucide-react';

const PlayerStats = ({ teams, matches }) => {
  const [topScorers, setTopScorers] = useState([]);
  const [playersByTeam, setPlayersByTeam] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  
  useEffect(() => {
    // Calculate top scorers and player stats
    const calculatePlayerStats = () => {
      const playerStats = {};
      
      // Gather all players from all teams
      teams.forEach(team => {
        team.players.forEach(player => {
          playerStats[player.id] = {
            id: player.id,
            name: player.name,
            number: player.number,
            position: player.position,
            teamId: team.id,
            teamName: team.name,
            teamColor: team.color,
            goals: 0,
            assists: 0,
            yellowCards: 0,
            redCards: 0,
            gamesPlayed: 0
          };
        });
      });
      
      // Process match events to update stats
      matches.forEach(match => {
        // Get players who participated in this match
        const homeTeamPlayers = match.homeTeam?.players || [];
        const awayTeamPlayers = match.awayTeam?.players || [];
        const allMatchPlayers = [...homeTeamPlayers, ...awayTeamPlayers];
        
        // Update games played for all players in this match
        allMatchPlayers.forEach(player => {
          if (playerStats[player.id]) {
            playerStats[player.id].gamesPlayed += 1;
          }
        });
        
        // Process events
        match.events.forEach(event => {
          if (event.type === 'goal' && event.playerId && playerStats[event.playerId]) {
            playerStats[event.playerId].goals += 1;
          }
          
          if (event.type === 'assist' && event.playerId && playerStats[event.playerId]) {
            playerStats[event.playerId].assists += 1;
          }
          
          if (event.type === 'yellowCard' && event.playerId && playerStats[event.playerId]) {
            playerStats[event.playerId].yellowCards += 1;
          }
          
          if (event.type === 'redCard' && event.playerId && playerStats[event.playerId]) {
            playerStats[event.playerId].redCards += 1;
          }
        });
      });
      
      // Convert to array and sort by goals
      const playerStatsArray = Object.values(playerStats);
      const sortedByGoals = [...playerStatsArray].sort((a, b) => b.goals - a.goals);
      
      // Group players by team
      const groupedByTeam = playerStatsArray.reduce((acc, player) => {
        if (!acc[player.teamId]) {
          acc[player.teamId] = [];
        }
        acc[player.teamId].push(player);
        return acc;
      }, {});
      
      setTopScorers(sortedByGoals.slice(0, 10)); // Top 10 scorers
      setPlayersByTeam(groupedByTeam);
      setFilteredPlayers(playerStatsArray);
    };
    
    calculatePlayerStats();
  }, [teams, matches]);
  
  // Handle search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPlayers(Object.values(playersByTeam).flat());
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = Object.values(playersByTeam)
      .flat()
      .filter(player => 
        player.name.toLowerCase().includes(term) || 
        player.teamName.toLowerCase().includes(term) ||
        player.position.toLowerCase().includes(term)
      );
    
    setFilteredPlayers(filtered);
  }, [searchTerm, playersByTeam]);
  
  // Get position label
  const getPositionLabel = (position) => {
    switch (position) {
      case 'GK': return 'Goalkeeper';
      case 'DEF': return 'Defender';
      case 'MID': return 'Midfielder';
      case 'FWD': return 'Forward';
      default: return position;
    }
  };
  
  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-lg font-semibold mb-3 flex items-center">
          <Award size={20} className="mr-1" />
          Top Goal Scorers
        </h2>
        
        <div className="space-y-2">
          {topScorers.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No goals scored yet
            </div>
          ) : (
            topScorers.map((player, index) => (
              <div 
                key={player.id}
                className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="w-6 flex justify-center mr-2">
                  <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium">
                    {index + 1}
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="font-medium">{player.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                    <div 
                      className="w-2 h-2 rounded-full mr-1" 
                      style={{ backgroundColor: player.teamColor }}
                    />
                    {player.teamName} • {getPositionLabel(player.position)}
                  </div>
                </div>
                
                <div className="text-lg font-bold">
                  {player.goals}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
      
      <Card>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold flex items-center">
            <Users size={20} className="mr-1" />
            All Players
          </h2>
          
          <div className="relative">
            <Search size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search players..."
              className="pl-8 pr-3 py-1 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th className="text-left py-2 px-2">Player</th>
                <th className="text-center py-2 px-2">GP</th>
                <th className="text-center py-2 px-2">G</th>
                <th className="text-center py-2 px-2">A</th>
                <th className="text-center py-2 px-2">YC</th>
                <th className="text-center py-2 px-2">RC</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map(player => (
                <tr key={player.id} className="border-b dark:border-gray-700 last:border-0">
                  <td className="py-2 px-2">
                    <div>
                      <div className="font-medium">{player.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                        <div 
                          className="w-2 h-2 rounded-full mr-1" 
                          style={{ backgroundColor: player.teamColor }}
                        />
                        {player.teamName} • {getPositionLabel(player.position)}
                      </div>
                    </div>
                  </td>
                  <td className="text-center py-2 px-2">{player.gamesPlayed}</td>
                  <td className="text-center py-2 px-2 font-medium">{player.goals}</td>
                  <td className="text-center py-2 px-2">{player.assists}</td>
                  <td className="text-center py-2 px-2">
                    {player.yellowCards > 0 && (
                      <span className="inline-block w-4 h-4 bg-yellow-300 rounded-sm" />
                    )}
                  </td>
                  <td className="text-center py-2 px-2">
                    {player.redCards > 0 && (
                      <span className="inline-block w-4 h-4 bg-red-500 rounded-sm" />
                    )}
                  </td>
                </tr>
              ))}
              
              {filteredPlayers.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-4 text-center text-gray-500 dark:text-gray-400">
                    No players found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PlayerStats;
